import React, { useState } from "react";
import { useUsers } from "../../Query/useUsers";
import {
  Autocomplete,
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import colors from "../../Constants/colors";
import { useTranslation } from "react-i18next";
import { User } from "../../Models/user";
import { useWorkspaceColors } from "../../Contexts/WorkspaceColorContext";

const WorkspaceAdminId: React.FC<{
  currentAdminEmail: string | null;
  onChange: (userId: number | null) => void;
}> = ({ currentAdminEmail, onChange }) => {
  const { t } = useTranslation("workspaces");
  const { colors: workspace } = useWorkspaceColors();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedEmail, setSelectedEmail] = useState<string | null>(null);

  const { data, isFetching } = useUsers(1, 10, searchTerm);

  const handleInputChange = (_: unknown, newValue: string) => {
    setSearchTerm(newValue);
  };

  const handleChange = (
    _: React.SyntheticEvent,
    value: string | User | null,
  ) => {
    if (typeof value === "object" && value !== null) {
      setSelectedEmail(value.email);
      onChange(value.id);
    } else {
      setSelectedEmail(null);
      onChange(null);
    }
  };

  const results = (data?.users || []).filter((u: User) => !u.workspace);
  const activeEmail = selectedEmail ?? currentAdminEmail;
  const initial = activeEmail ? activeEmail.charAt(0).toUpperCase() : "?";

  return (
    <Box>
      <Autocomplete
        freeSolo
        options={results}
        getOptionLabel={(option) =>
          typeof option === "string" ? option : option.email
        }
        onInputChange={handleInputChange}
        onChange={handleChange}
        loading={isFetching}
        size="small"
        renderInput={(params) => (
          <TextField
            {...params}
            label={t("searchAssignAdmin")}
            variant="outlined"
            InputLabelProps={{ shrink: true }}
            sx={{
              "& .MuiOutlinedInput-root": {
                fontSize: "0.875rem",
                borderRadius: 2,
                bgcolor: "white",
                color: colors.primary_dark,
                "& fieldset": { borderColor: colors.gray300 },
                "&:hover fieldset": {
                  border: `0.5px solid ${workspace.primary_dark}`,
                },
                "&.Mui-focused fieldset": {
                  border: `0.5px solid ${workspace.primary_dark}`,
                  borderLeft: `4px solid ${workspace.primary_dark}`,
                },
              },
              "& .MuiInputBase-input": { color: colors.primary_dark },
              "& .MuiInputLabel-root": {
                color: `${colors.gray600} !important`,
                fontSize: "0.875rem",
              },
              '& .MuiInputLabel-root[data-shrink="true"]': {
                backgroundColor: "white",
                px: 0.5,
              },
              "& .MuiInputLabel-root.Mui-focused": {
                color: `${workspace.primary_dark} !important`,
              },
            }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isFetching ? (
                    <CircularProgress
                      size={16}
                      sx={{ color: workspace.accent }}
                    />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            inputProps={{ ...params.inputProps, autoComplete: "off" }}
          />
        )}
        renderOption={(props, option) => (
          <li
            {...props}
            key={typeof option === "string" ? option : option.id}
            style={{
              padding: "8px 14px",
              backgroundColor: colors.white,
              color: colors.primary_dark,
              fontSize: "0.875rem",
              cursor: "pointer",
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              opacity:
                typeof option !== "string" && option.workspace ? 0.6 : 1,
            }}
          >
            {typeof option === "string" ? option : option.email}
            {typeof option !== "string" && option.workspace && (
              <Typography
                variant="caption"
                sx={{ color: colors.gray500, fontSize: "0.75rem" }}
              >
                {t("workspaceLabel", {
                  workspaceName: option.workspace.name,
                })}
              </Typography>
            )}
          </li>
        )}
        noOptionsText={
          <Typography sx={{ color: colors.gray500, fontSize: "0.875rem" }}>
            {t("noUsersFound")}
          </Typography>
        }
        slotProps={{
          paper: {
            sx: {
              bgcolor: `${colors.white} !important`,
              border: `1px solid ${colors.gray200}`,
              boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
              "& .MuiAutocomplete-listbox": {
                padding: 0,
                bgcolor: colors.white,
                "& .MuiAutocomplete-option": {
                  bgcolor: `${colors.white} !important`,
                  "&:hover": { bgcolor: `${colors.gray50} !important` },
                  "&.Mui-focused": { bgcolor: `${colors.gray50} !important` },
                },
              },
            },
          },
        }}
      />

      <Box sx={{ mt: 1.5 }}>
        {activeEmail ? (
          <Chip
            avatar={
              <Avatar
                sx={{
                  bgcolor: `${workspace.primary_dark} !important`,
                  color: `${colors.white} !important`,
                  fontSize: "0.75rem",
                  fontWeight: 600,
                }}
              >
                {initial}
              </Avatar>
            }
            label={
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography
                  variant="caption"
                  sx={{ color: colors.gray500, fontSize: "0.7rem", lineHeight: 1 }}
                >
                  {selectedEmail
                    ? t("selectedAdminLabel")
                    : t("currentAdminLabel")}
                </Typography>
                <Typography
                  sx={{
                    color: colors.primary_dark,
                    fontSize: "0.85rem",
                    fontWeight: 500,
                  }}
                >
                  {activeEmail}
                </Typography>
              </Box>
            }
            sx={{
              bgcolor: colors.gray50,
              border: `1px solid ${colors.gray200}`,
              height: "auto",
              py: 0.5,
              px: 0.5,
              "& .MuiChip-label": { px: 1 },
            }}
          />
        ) : (
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{
              color: colors.gray500,
              fontSize: "0.8rem",
            }}
          >
            <PersonOutlineIcon sx={{ fontSize: 16 }} />
            <Typography sx={{ fontSize: "0.8rem", color: colors.gray500 }}>
              {t("noAdminAssigned")}
            </Typography>
          </Stack>
        )}
      </Box>
    </Box>
  );
};

export default WorkspaceAdminId;
